import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import constants from '../../utils/constants'
import './AlertPlus.css'
import SiteCard from './SiteCard/SiteCard'
import AlertBubble from './AlertBubble/AlertBubble'
import SiteSelectionModal from './SiteSelectionModal/SiteSelectionModal';
import API from '../../utils/API';
import useWebSocket from '../../components/common/useWebSocket';
import Toast from '../../components/Toast/Toast';
import templateInstructions from '../../utils/templateInstructions';
import { trackEvent } from '../../utils/analytics';

function AlertPlus(props) {
  const navigate = useNavigate();
  const websocket = useWebSocket();

  const location = useLocation();
  const { user } = props;
  const [sites, setSites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [alertTemplates, setAlertTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const hasTrackedRef = useRef(false);

  // Check if all template instructions are valid
  const validTemplateIds = new Set(templateInstructions.map(instruction => instruction.id));

  const permissionIds = user.permissions.map((permission)=>{
    return permission.id
  });
  
  // Redirect user if they don't have alert plus manage permission
  useEffect(() => {
    if (!permissionIds.includes(constants.permissionIds.aPlusOfficerApp)) {
      navigate('/forbidden');
    }
  }, [navigate, permissionIds]);

  // Get location state to display success or error message
  useEffect(() => {
    if (location.state) {
      if (location.state.success) {
        setSuccess(<FormattedMessage id={location.state.success} />);
      }
      if (location.state.error) {
        setError(<FormattedMessage id={location.state.error} />);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (!websocket) return;

    const handleWebSocketMessage = (message) => {
      const data = JSON.parse(message.data);
      if (data.messageId === 5001) {
        fetchSitesAndAlerts();
      }
    };

    websocket.addEventListener('message', handleWebSocketMessage);

    return () => {
      websocket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [websocket]);

  const consolidateRequests = (requests) => {
    return requests.reduce((acc, request) => {
      if (!acc[request.alertTemplateId]) {
        acc[request.alertTemplateId] = [];
      }
      acc[request.alertTemplateId].push(request);
      return acc;
    }, {});
  };
  
  /**
   * Fetch sites and alerts data
   */
  const fetchSitesAndAlerts = async () => {
    try {
      const [sitesResponse, alertsResponse, templatesResponse] = await Promise.all([
        API.getSites(),
        API.getAlerts(),
        API.getAlertPlusTemplates(),
      ]);
      const filteredTemplates = templatesResponse.data.filter(template => validTemplateIds.has(template.id));
      const sitesWithConsolidatedRequests = sitesResponse.data.map(site => {
        const consolidatedRequests = consolidateRequests(site.requests);
        return { ...site, consolidatedRequests };
      });
      console.log('sites:', sitesWithConsolidatedRequests);
      // console.log('alerts:', alertsResponse.data);
      // console.log('templates:', filteredTemplates);
      setSites(sitesWithConsolidatedRequests);
      setAlerts(alertsResponse.data);
      setAlertTemplates(filteredTemplates);

      // Track dashboard view once on initial load
      if (!hasTrackedRef.current && sitesResponse.data.length > 0) {
        hasTrackedRef.current = true;
        const sitesWithActiveAlerts = sitesResponse.data.filter(site => site.alert_in_progress).length;
        const totalPendingRequests = sitesResponse.data.reduce((sum, site) => sum + (site.requests?.length || 0), 0);

        trackEvent('alert_plus.dashboard', {
          total_sites: sitesResponse.data.length,
          sites_with_active_alerts: sitesWithActiveAlerts,
          total_pending_requests: totalPendingRequests,
          available_templates: filteredTemplates.length
        }, user);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSitesAndAlerts();
  }, []);

  /**
   * Handle open modal and set selected template
   * @param {*} template 
   */
  const handleOpenModal = (template) => {
    setSelectedTemplate(template);
  };

  /**
   * Handle refresh data after triggering alert
   */
  const refreshData = () => {
    console.log('Refreshing data...');
    // Update state or re-fetch data
    fetchSitesAndAlerts();
  };

  return (
    <>
      <div className="title">
        <FormattedMessage id={constants.pageType.ManageAlerts} />
      </div>
      <div className="content">
        <div className="site-cards">
          {sites.length === 0 && (
            <div className="no-sites card">
              <FormattedMessage id="NO_SITES" />
            </div>
          )}
          {/* Render SiteCard components */}
          {sites.map((site) => (
            <SiteCard
              key={site.id}
              site={site}
              alertTemplates={alertTemplates}
              refreshData={refreshData}
              setSuccess={setSuccess}
              setError={setError}
            />
          ))}
        </div>
        <div className="alert-bubbles">
          <div className="title trigger-title">
            <FormattedMessage id='TRIGGER_ALERT' />
          </div>
          {/* Render AlertBubble components */}
          {alertTemplates.map((template) => (
            <AlertBubble
              key={template.id}
              template={template}
              sites={sites}
              onOpenModal={handleOpenModal}
            />
          ))}
        </div>
      </div>
      <SiteSelectionModal
        sites={sites}
        selectedTemplate={selectedTemplate}
        refreshData={refreshData}
        setSuccess={setSuccess}
        setError={setError}
      />
      <Toast
        success={success}
        error={error}
        setSuccess={setSuccess}
        setError={setError}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(AlertPlus);
